import { Box, Button } from "@mui/material";
import { useControlledState } from "@react-stately/utils";
import { useCallback, useState } from "react";

import { Dialog, DialogActions } from "@/components/ui-v2";
import type { NonEmptyArray, VerticalAlignment } from "@/types";

import { DriveProvider } from "./DriveContext";
import { DriveManager } from "./DriveManager";
import { DriveItem, DriveItemType } from "./types";
import { useStateBasedDriveSearchParams } from "./utils";

type DriveManagerDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  initialDirectory?: string;
  selectableItemTypes: NonEmptyArray<DriveItemType>;
  selectableItemCount?: number;
  selectedItems?: DriveItem[];
  setSelectedItems?: (items: DriveItem[]) => void;
  disabledItems?: DriveItem[];
  onConfirmSelection?: (items: DriveItem[]) => void;
  onConfirmDirectorySelection?: (directoryPath: string) => void;
  popoverOriginVertical?: number | VerticalAlignment;
  confirmText?: string;
};

export default function DriveManagerDialog({
  isOpen,
  onClose,
  initialDirectory = "/",
  selectableItemTypes,
  selectableItemCount,
  selectedItems,
  setSelectedItems,
  disabledItems,
  onConfirmSelection,
  onConfirmDirectorySelection,
  confirmText = "Confirm",
}: DriveManagerDialogProps) {
  const [currentDirectory, setCurrentDirectory] = useState(initialDirectory);
  const [internalSelectedItems, setInternalSelectedItems] = useControlledState<
    DriveItem[]
  >(selectedItems, [], setSelectedItems);
  const [searchParams, setSearchParams] = useStateBasedDriveSearchParams();

  const activeButtonClasses =
    "bg-violet-600 border-violet-700/25 text-violet-100 hover:bg-violet-700 hover:border-violet-800/25 dark:hover:bg-violet-500 dark:hover:border-violet-400/25 hover:text-violet-50";
  const inactiveButtonClasses =
    "bg-violet-600/25 border-violet-700/25 text-violet-100/25 cursor-default pointer-events-none shadow-none";

  const selectedDirectoryPath =
    internalSelectedItems?.length === 1 &&
    internalSelectedItems[0].__typename === "Directory"
      ? internalSelectedItems[0].path
      : currentDirectory;

  const selectedPath = selectedDirectoryPath ?? "Drive";
  const selectedDirectoryName = selectedPath.split("/").pop() || "/";

  const onConfirmSelectionAndClose = useCallback(
    (items: DriveItem[]) => {
      onConfirmSelection?.(items);
      onClose();
    },
    [onConfirmSelection, onClose],
  );

  const handleActivateItem = useCallback(
    (item: DriveItem) => {
      switch (item.__typename) {
        case "Directory":
          setCurrentDirectory(item.path);
          break;
        case "Style":
        case "ReferenceSet":
        case "Asset":
        case "File":
          if (selectableItemTypes.includes(item.__typename)) {
            onConfirmSelectionAndClose([item]);
          }
          break;
      }
    },
    [setCurrentDirectory, selectableItemTypes, onConfirmSelectionAndClose],
  );

  const handleClose = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      keepMounted={false}
      TransitionProps={{
        timeout: {
          enter: 200,
          exit: 100,
        },
        unmountOnExit: true,
      }}
      sx={{
        "& .MuiPaper-root": {
          display: "flex",
          flexDirection: "column",
          width: "calc(100% - 32px)",
          height: "100%",
          padding: "0px",
          maxWidth: "100%",
          borderRadius: "8px",
        },
      }}
    >
      <Box
        sx={{
          height: "100%",
          padding: "0px",
        }}
      >
        <DriveProvider
          currentDirectoryPath={currentDirectory}
          setCurrentDirectoryPath={setCurrentDirectory}
          selectableItemTypes={selectableItemTypes}
          selectableItemCount={selectableItemCount}
          selectedItems={internalSelectedItems}
          setSelectedItems={setInternalSelectedItems}
          onActivateItem={handleActivateItem}
          disabledItems={disabledItems}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
        >
          <DriveManager viewLocation="dialog" />
        </DriveProvider>
      </Box>
      <DialogActions
        sx={{
          position: "fixed",
          width: "fit-content",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          left: 0,
          right: 0,
          bottom: "32px",
          margin: "0 auto",
          background: "secondary.6",
          boxShadow: "inset 0 0 0 0.5px secondary.700/50",
          borderRadius: 9999,
        }}
      >
        {onConfirmSelection ? (
          <>
            <Button onClick={handleClose} variant="contained">
              Cancel
            </Button>
            <Button
              onClick={(event) => {
                event.stopPropagation();
                onConfirmSelectionAndClose(internalSelectedItems);
              }}
              sx={{
                opacity: internalSelectedItems?.length > 0 ? 1 : 0.5,
                pointerEvents:
                  internalSelectedItems?.length > 0 ? "all" : "none",
              }}
              variant="contained"
            >
              {confirmText}
            </Button>
          </>
        ) : onConfirmDirectorySelection ? (
          <>
            <Button onClick={handleClose} variant="contained">
              Cancel
            </Button>
            <Button
              onClick={(event) => {
                event.stopPropagation();
                onConfirmDirectorySelection(selectedPath);
                onClose();
              }}
              className={activeButtonClasses}
              variant="contained"
            >
              <span className="normal-case">
                {confirmText} to{" "}
                <strong>
                  {selectedDirectoryName === "/"
                    ? "Drive"
                    : selectedDirectoryName}
                </strong>
              </span>
            </Button>
          </>
        ) : (
          <Button
            onClick={handleClose}
            className={
              internalSelectedItems?.length > 0
                ? activeButtonClasses
                : inactiveButtonClasses
            }
            variant="contained"
          >
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
